/* @flow */
import React, {
  createContext, useState, useEffect, useContext, useCallback, useMemo,
} from 'react';
import { find, filter } from 'lodash/fp';

import loadThemeOnce, {
  save, saveAutoDarkMode, useDarkMode, supportsDarkMode, applyThemeGlobals,
} from './utils';
import Themes from './Themes';


export const defaultTheme = {
  setTheme: () => {},
  toggleAutoDarkMode: () => {},
  ...Themes[0],
};

export const shadows = {
  header: {
    shadowColor: 'rgba(0,0,0,0.5)',
    shadowOpacity: 0.5,
    shadowOffset: {
      height: 0,
    },
    shadowRadius: 3,
  },
};

const ThemeContext = createContext<ThemeContextData>(defaultTheme);

export const { Provider, Consumer } = ThemeContext;

export const ThemeProvider = ({ children }: Object) => {
  const [theme, setThemeInternal] = useState(defaultTheme),
        [isLoaded, setIsLoaded] = useState(false),
        [autoDarkMode, setAutoDarkMode] = useState(true),
        isDark = useDarkMode(),
        { family } = theme,
        themesInFamily = filter((t) => t.family === family)(Themes),
        darkTheme = useMemo(() => supportsDarkMode && autoDarkMode
          ? find((t) => isDark ? t.flavor === 'dark' : t.flavor === 'light')(themesInFamily) || theme
          : theme, [autoDarkMode, isDark, theme, themesInFamily]),
        toggleAutoDarkMode = useCallback(() => {
          setAutoDarkMode((wasEnabled) => {
            const isEnabled = !wasEnabled;
            saveAutoDarkMode(isEnabled);
            return isEnabled;
          });
        }, []),
        setTheme = useCallback((newTheme) => {
          setThemeInternal(newTheme);
          save(newTheme);
        }, []);

  useEffect(() => {
    const fn = async () => {
      const [t, adm] = await loadThemeOnce();
      if (t) {
        setThemeInternal(t);
      }
      if (adm !== undefined) {
        setAutoDarkMode(adm);
      }
      setIsLoaded(true);
    };
    fn();
  }, []);


  useEffect(() => {
    if (theme) {
      applyThemeGlobals(theme);
    }
  }, [theme]);

  const providedValue = useMemo(() => ({
    theme: darkTheme,
    isLoaded,
    toggleAutoDarkMode,
    autoDarkMode,
    setTheme,
  }), [autoDarkMode, darkTheme, isLoaded, setTheme, toggleAutoDarkMode]);

  return (
    <Provider value={providedValue}>
      { children }
    </Provider>
  );
};

export const useTheme = () => {
  const { theme } = useContext(ThemeContext);
  return theme;
};

export const useColors = () => {
  const { theme: { colors } } = useContext(ThemeContext);
  return colors;
};

export const useColor = (color: string) => {
  const { theme: { colors } } = useContext(ThemeContext);
  return colors[color];
};

export const withTheme = (WrappedComponent: React.Component<any, any> | React.PureComponent<any, any>, propName = 'theme') => function withThemeHOC(props: any) {
  return (
    <Consumer>
      {({ theme }) => {
        const themeProps = { [propName]: theme };
        return (
          <WrappedComponent
            {...props}
            {...themeProps}
          />
        );
      }}
    </Consumer>
  );
};

export default ThemeContext;
